import {isUndefined} from 'lodash';
import {compile, match} from 'path-to-regexp';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {makeMapStateToProps} from '../../helpers/reselectHelpers';
import {SettingsRouteFindResult, settingsRoutes} from '../../settings/settingsRoutes';
import {getCompanyUrl} from '../../store/data/company/companySelectors';
import {getOptimisticData} from '../../store/data/dataSelectors';
import {getHasLazyTeammates, getInitialFeatureFlags} from '../../store/featureFlags/featureFlagsSelectors';
import {makeGetOptionalLegacyPath} from '../../store/panels/legacy/legacyPanelSelectors';
import {AppPanelId, makeLegacyPanelId} from '../../store/panels/panelStateBase';
import {LegacyFrame} from './legacyFrame';
import {legacySettingsRewriteRoutes} from './legacyRewriteRoutes';

/*
 * Props.
 */

interface LegacyFrameContainerProps {
  panelId: AppPanelId;
}

/*
 * Selectors.
 */

function getLegacyPanelId(_state: unknown, props: LegacyFrameContainerProps) {
  return makeLegacyPanelId(props.panelId);
}

function makeFindNewLegacyRoute() {
  return createSelector(
    [getInitialFeatureFlags, getOptimisticData],
    (initialFlags, dataState) => (currentPath: string | undefined) => {
      if (isUndefined(currentPath)) {
        return undefined;
      }

      return settingsRoutes.reduce<SettingsRouteFindResult | undefined>((findResult, route) => {
        if (findResult) {
          return findResult;
        }

        const matchResult = matchPathOnly(currentPath, route.path);
        if (!matchResult) {
          return undefined;
        }

        if (route.featureFlag && !initialFlags[route.featureFlag]) {
          return undefined;
        }

        const matchProps = {
          ...matchResult,
          path: route.path,
          url: currentPath,
        };

        if (route.canBeRendered && !route.canBeRendered(matchProps, dataState)) {
          return undefined;
        }

        return {
          route,
          match: matchProps,
        };
      }, undefined);
    },
  );
}

function makeFindRewriteLegacyRoute() {
  return createSelector([getInitialFeatureFlags], (initialFlags) => (currentPath: string | undefined) => {
    if (isUndefined(currentPath)) {
      return undefined;
    }

    for (const route of legacySettingsRewriteRoutes) {
      if (route.featureFlag && !initialFlags[route.featureFlag]) {
        continue;
      }

      const matchResult = matchPathOnly(currentPath, route.legacyPath);
      if (!matchResult) {
        continue;
      }

      const [newPath, newPathParams] = route.newPath.split('?');
      return compile(newPath)(matchResult.params) + (newPathParams ? `?${newPathParams}` : '');
    }
    return undefined;
  });
}

function matchPathOnly(pathAndQuery: string, route: string) {
  return match(route.split('?')[0])(pathAndQuery.split('?')[0]);
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<LegacyFrameContainerProps>()(() => ({
  legacyPath: makeGetOptionalLegacyPath(getLegacyPanelId),
  companyUrl: getCompanyUrl,
  findNewLegacyRoute: makeFindNewLegacyRoute(),
  findRewriteLegacyRoute: makeFindRewriteLegacyRoute(),
  hasLazyTeammates: getHasLazyTeammates,
}));

export const LegacyFrameContainer = connect(mapStateToProps)(LegacyFrame);
